// While the Director works: a calm focus ring whose split converges as the
// video comes together, the estimate, and a trace of the stages behind it.
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Check } from "lucide-react";
import { estimate, STAGES, type TurnProgress } from "../shared/progress";
import { LINE, type Place } from "../shared/line";
import { bilingual, text, useText } from "./i18n";
import { tween } from "./fx";

const TEXT = bilingual({
  en: {
    making: "Making your video",
    progress: (percent: number, stage: string) => `${percent}%, ${stage}`,
    seconds: (n: number) => `${n} s`,
    minutes: (n: number) => `${n} min`,
    worked: (time: string) => `Worked for ${time}`,
  },
  es: {
    making: "Creando tu vídeo",
    progress: (percent: number, stage: string) => `${percent} %, ${stage}`,
    seconds: (n: number) => `${n} s`,
    minutes: (n: number) => `${n} min`,
    worked: (time: string) => `Ha trabajado ${time}`,
  },
});

/** Re-renders every second while `on`. */
function useClock(on: boolean) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

/** Counts up to each new value instead of jumping. */
function useCountUp(target: number) {
  const [value, setValue] = useState(target),
    shown = useRef(target);
  useEffect(() => {
    const from = shown.current;
    if (from === target) return;
    let live = true;
    void tween(Math.min(1600, 400 + 80 * Math.abs(target - from)), (k) => {
      if (!live) return;
      shown.current = Math.round(from + (target - from) * (1 - (1 - k) ** 3));
      setValue(shown.current);
    });
    return () => {
      live = false;
    };
  }, [target]);
  return value;
}

/** The running turn as the customer sees it: a percentage, the stage it is
 * in and the whole minutes since it started. */
export function useMaking(progress?: TurnProgress) {
  const live = !!progress && !progress.endedAt,
    now = useClock(live),
    percent = useCountUp(
      progress ? Math.round(estimate(progress, now) * 100) : 0,
    ),
    step = progress?.steps.at(-1)?.stage,
    stages = useText(STAGES);
  return {
    percent,
    stage: step ? stages[step].doing : stages.prepare.doing,
    minutes: progress
      ? Math.floor((now - Date.parse(progress.startedAt)) / 60_000)
      : 0,
  };
}

// Specks of light drifting up through the frame, like dust in a projector beam.
const DUST = [
  [8, 1.6, 31, -3, 0.5, "255,255,255", 3],
  [19, 2.6, 38, -21, 0.28, "37,244,238", -4],
  [31, 1.2, 27, -12, 0.55, "255,255,255", 5],
  [44, 3.4, 44, -30, 0.18, "255,255,255", -6],
  [57, 1.4, 29, -7, 0.45, "254,44,85", 4],
  [66, 2.2, 35, -17, 0.35, "255,255,255", -3],
  [78, 1.1, 25, -1, 0.5, "255,255,255", 6],
  [87, 2.8, 41, -26, 0.22, "254,44,85", -5],
  [93, 1.5, 33, -14, 0.4, "37,244,238", 2],
].map(
  ([x, z, d, w, o, c, sx]) =>
    ({
      "--x": `${x}%`,
      "--z": `${z}cqw`,
      "--d": `${d}s`,
      "--w": `${w}s`,
      "--o": o,
      "--c": `rgba(${c},0.9)`,
      "--sx": `${sx}cqw`,
    }) as CSSProperties,
);

/** The focus ring: cyan and red copies orbit apart and close in on the white
 * ring as the work advances; its arc is the estimate. Without one, a short arc
 * turns slowly. */
function Lens({ percent }: { percent?: number }) {
  const p = (percent ?? 30) / 100,
    split = 0.3 + 4 * (1 - p);
  return (
    <div className="mk-lens" aria-hidden="true">
      <div className="mk-orbit">
        <svg
          className="mk-c"
          viewBox="0 0 100 100"
          style={{ transform: `translateX(${split}cqw)` }}
        >
          <circle cx="50" cy="50" r="46" />
        </svg>
        <svg
          className="mk-r"
          viewBox="0 0 100 100"
          style={{ transform: `translateX(${-split}cqw)` }}
        >
          <circle cx="50" cy="50" r="46" />
        </svg>
      </div>
      <svg
        className={`mk-arc ${percent === undefined ? "idle" : ""}`}
        viewBox="0 0 100 100"
      >
        <circle className="tr" cx="50" cy="50" r="46" />
        <circle
          className="pr"
          cx="50"
          cy="50"
          r="46"
          pathLength="100"
          style={{ strokeDashoffset: 100 - (percent ?? 22) }}
        />
      </svg>
      {percent !== undefined && (
        <span className="mk-head" style={{ rotate: `${percent * 3.6}deg` }} />
      )}
    </div>
  );
}

/** The phone screen while a version waits in line: its place and roughly how long. */
function LineScreen({ place, text }: { place: Place | null; text: string }) {
  const l = useText(LINE);
  return (
    <div className="making" role="status" aria-label={text}>
      <div className="mk-aura" aria-hidden="true">
        <i />
        <i />
      </div>
      <div className="mk-dust" aria-hidden="true">
        {DUST.map((style, i) => (
          <i key={i} style={style} />
        ))}
      </div>
      <Lens />
      {place && (
        <span
          className="mk-num"
          aria-hidden="true"
          style={{ "--s": "0.5cqw" } as CSSProperties}
        >
          <b>{place.place}</b>
          <small>{l.suffix(place.place)}</small>
        </span>
      )}
      <p className="mk-cap" aria-hidden="true">
        <span key={text}>
          {place
            ? place.minutes === null
              ? l.inLine
              : `${l.inLine} · ${l.about(place.minutes)}`
            : text}
        </span>
      </p>
    </div>
  );
}

/** The phone screen while a version is being made, or waits in line to be. */
export function MakingScreen({
  progress,
  inLine,
}: {
  progress?: TurnProgress;
  /** Its place in line (null until known) and that place said. */
  inLine?: { place: Place | null; text: string };
}) {
  if (inLine) return <LineScreen {...inLine} />;
  return <Making progress={progress} />;
}

function Making({ progress }: { progress?: TurnProgress }) {
  const t = useText(TEXT),
    { percent, stage, minutes } = useMaking(progress),
    s = 0.12 + 0.6 * (1 - percent / 100);
  return (
    <div
      className="making"
      role="progressbar"
      aria-label={t.making}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-valuetext={t.progress(percent, stage)}
    >
      <div className="mk-aura" aria-hidden="true">
        <i />
        <i />
      </div>
      <div className="mk-dust" aria-hidden="true">
        {DUST.map((style, i) => (
          <i key={i} style={style} />
        ))}
      </div>
      <Lens percent={percent} />
      <span
        className="mk-num"
        aria-hidden="true"
        style={{ "--s": `${s}cqw` } as CSSProperties}
      >
        <b>{percent}</b>
        <small>%</small>
      </span>
      <p className="mk-cap" aria-hidden="true">
        <span key={stage}>{stage}</span>
        {minutes > 0 && <small>{t.minutes(minutes)}</small>}
      </p>
    </div>
  );
}

/** A thumbnail being worked on: its last poster, dimmed, under the ring. */
export function MakingThumb({
  poster,
  percent,
}: {
  poster?: string;
  percent?: number;
}) {
  return (
    <span className="making mini" aria-hidden="true">
      {poster && <img src={poster} alt="" />}
      <span className="mk-aura">
        <i />
        <i />
      </span>
      <Lens percent={percent} />
    </span>
  );
}

const span = (from: string, to: string) => {
  const s = Math.max(1, (Date.parse(to) - Date.parse(from)) / 1000),
    t = text(TEXT);
  return s < 60 ? t.seconds(Math.round(s)) : t.minutes(Math.round(s / 60));
};

/** What the Director has done so far: one line per stage, the current one
 * with its latest action. Once the turn ends it folds into one line. */
export function Trace({
  progress,
  activity,
}: {
  progress: TurnProgress;
  activity: string;
}) {
  const t = useText(TEXT),
    stages = useText(STAGES);
  const { steps, endedAt } = progress;
  const list = (
    <ol className="trace-steps">
      {steps.map((s, i) => {
        const next = steps[i + 1]?.at || endedAt,
          on = !next,
          label = on ? stages[s.stage].doing : stages[s.stage].done;
        return (
          <li key={s.stage} className={on ? "on" : "done"}>
            <span className="trace-dot">
              {on ? <i /> : <Check size={10} strokeWidth={3} />}
            </span>
            <span className="trace-t">{label}</span>
            {next && <span className="trace-time">{span(s.at, next)}</span>}
            {on && activity && activity !== label && (
              <small className="trace-now" key={activity}>
                {activity}
              </small>
            )}
          </li>
        );
      })}
    </ol>
  );
  if (!endedAt) return <div className="trace">{list}</div>;
  return (
    <details className="trace ended">
      <summary>{t.worked(span(progress.startedAt, endedAt))}</summary>
      {list}
    </details>
  );
}
